import type { Env, Platform } from '@sparklayer-io/spark-dash-core/context/env-context';
import { resolvePath } from '@sparklayer-io/spark-dash-core/lib/navigation';
import { getShopifyAppSetupLink } from '@sparklayer-io/spark-dash-core/lib/shopify';
import type {
  NavigationHandlerContract,
  NavigationOptions,
  PathTree,
} from '@sparklayer-io/spark-dash-core/types/handlers/navigation';

export type HostNavContext = {
  /** The site the current page is mounted under (its route params). */
  siteId: string;
  siteEnv: string;
  env: Env | null;
};

// The parts of the TanStack router the navigation handler drives; an interface so the handler does
// not depend on the router class generics.
type HostRouter = {
  navigate: (opts: { to: string; search?: Record<string, string>; replace?: boolean }) => void;
  state: {
    location: { pathname: string; search: unknown };
    matches: ReadonlyArray<{ params?: Record<string, unknown> }>;
  };
};

// Tree is built per navigation because some links depend on the connected store.
function buildPaths(context: HostNavContext): PathTree<true> {
  const storeId = context.env?.store_id;

  return {
    home: '/',
    plan: { path: '/settings/plan', opts: { excludeSiteEnv: true } },
    features: { path: '/features' },
    onboarding: {
      setup: {
        path: {
          shopify: {
            path: getShopifyAppSetupLink(storeId),
            opts: { absolute: true, newTab: true },
          },
          '#default': '/',
        },
      },
    },
    pricelist: {
      view: '/pricing/lists',
      create: '/pricing/lists/create',
    },
    customerGroup: {
      create: '/customers/groups/create',
      quickSetup: '/customers/groups/quick-setup',
    },
  };
}

function platformOf(env: Env | null): Platform | undefined {
  return env?.platform ?? env?.connected_platform;
}

/**
 * Collapse a query object to the lone strings the handler contract promises (see the same
 * `toStringRecord` in src/lib/handlers/navigation.ts for why this exists).
 */
function toStringRecord(
  source: Record<string, string | null | (string | null)[] | undefined>,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(source)) {
    result[key] = (Array.isArray(value) ? value[0] : value) ?? '';
  }
  return result;
}

type RuntimeNavigate = (opts: {
  to: string;
  search?: Record<string, string>;
  hash?: string;
  replace?: boolean;
}) => void;

/**
 * Host implementation of the navigation contract pages consume, driving the TanStack router.
 * Targets are full paths, so a path missing from the host tree bounces to `/` for re-resolution.
 */
export function createHostNavigationHandler(
  router: HostRouter,
  context: HostNavContext,
): NavigationHandlerContract {
  const navigateTo: RuntimeNavigate = router.navigate as RuntimeNavigate;

  function qualify(path: string, options?: NavigationOptions): string {
    let site = '';
    if (!options?.excludeSiteId && context.siteId) {
      site += `/${context.siteId}`;
    }
    if (!options?.excludeSiteEnv && context.siteId && context.siteEnv) {
      site += `/${context.siteEnv}`;
    }
    return `${site}${path}`;
  }

  function urlFor(path: string, query?: Record<string, string>): string {
    const searchParams = new URLSearchParams(query);
    const queryString = searchParams.toString();
    return `${path}${queryString ? `?${queryString}` : ''}`;
  }

  function navigate(path: string, options?: NavigationOptions) {
    if (options?.absolute) {
      if (options.newTab) {
        window.open(urlFor(path, options.query), '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = urlFor(path, options.query);
      }
      return;
    }

    const fullPath = qualify(path, options);

    if (options?.newTab) {
      window.open(urlFor(fullPath, options.query), '_blank', 'noopener,noreferrer');
      return;
    }

    navigateTo({ to: fullPath, search: options?.query, replace: options?.replace });
  }

  function navigateById(
    pathId: Parameters<NavigationHandlerContract['navigateById']>[0],
    options?: NavigationOptions,
  ) {
    const { path, opts } = resolvePath(buildPaths(context), pathId, platformOf(context.env));
    navigate(path, { ...opts, ...options });
  }

  return {
    navigate,
    navigateById,
    params() {
      // Expose the matched route's params, then overlay the site pair the page is under.
      const matched = router.state.matches[router.state.matches.length - 1]?.params ?? {};
      const params: Record<string, string> = {};
      for (const [key, value] of Object.entries(matched)) {
        if (typeof value === 'string') params[key] = value;
      }
      if (context.siteId) params.siteId = context.siteId;
      if (context.siteEnv) params.siteEnv = context.siteEnv;
      return params;
    },
    query() {
      return toStringRecord(
        router.state.location.search as Record<string, unknown> as Record<
          string,
          string | null | (string | null)[] | undefined
        >,
      );
    },
    path() {
      return router.state.location.pathname;
    },
  };
}
